import { useCallback, useEffect, useRef, useState } from 'react'
import { FormatDateFunction, TranslationFunction } from '../common/types'
import LivePricingFormatter, { FormattedLivePricingDataTransport } from '../models/pricing/live-pricing-formatter'
import { PriceFormatter } from './price'
import PricingClient from '../clients/pricing-client'
import isNumber from 'lodash/isNumber'
import { getModel } from '../utils/model-utils'
import LivePricingResponseTransport from '../transports/live-pricing-transport'
import LivePricingModel from '../models/pricing/live-pricing-model'
import { uniq } from 'lodash'
import { usePropertyDetailsContext } from './property-details'

export interface LivePricing {
    pricingDetails: FormattedLivePricingDataTransport | null
    isLoading: boolean
    error: number | null
}

interface UseComboLivePricingState {
    comboPrices: Map<string, LivePricing>
}

interface useComboLivePricingParams {
    arrival: string | null
    departure: string | null
    childrenAges: string
    pets: number
    adults: number
}

enum LPS_ERROR_ENUM {
    GENERAL_ERROR = 0,
    INVALID_ARRIVAL_DAY = 61,
    INVALID_DEPARTURE_DAY = 62,
    TOO_MANY_PERSONS = 43,
    INVALID_ARRIVAL_DEPARTURE_DATE = 48,
}

export const useComboLivePricing = (
    params: useComboLivePricingParams,
    loadedObjectIds: string[],
    maxAllowedPerson = 12,
    formatDate: FormatDateFunction,
    t: TranslationFunction,
    formatPrice: PriceFormatter,
): UseComboLivePricingState => {
    const [state, setState] = useState<Map<string, LivePricing>>(new Map())
    const { isComboListing } = usePropertyDetailsContext()
    const livePricingFormatter = new LivePricingFormatter(formatDate, t, formatPrice)

    const prevIdsRef = useRef<string[]>([])

    const fetchComboPrices = useCallback(async () => {
        if (!isComboListing) {
            return
        }

        // Reset if reset (date changed)
        if (loadedObjectIds.length === 0) {
            setState(prev => {
                if (prev.size === 0) {
                    return prev
                }
                const updated = new Map()
                return updated
            })
            prevIdsRef.current = []
            return
        }

        // Determine new IDs to fetch price
        const prevIds = prevIdsRef.current
        const newIds = uniq(loadedObjectIds.filter(id => !prevIds.includes(id)))

        if (newIds.length === 0) {
            return
        }

        // Add newIds to the ref
        newIds.forEach(id => prevIds.push(id))

        // Mark new IDs as loading
        setState(prev => {
            const updated = new Map(prev)
            newIds.forEach(id => {
                updated.set(id, { pricingDetails: null, isLoading: true, error: null })
            })
            return updated
        })

        try {
            const response = await PricingClient.getComboLivePrices({
                params: {
                    listingIds: newIds.map(id => Number(id)).join(','),
                    ...params,
                },
            })

            if (response?.data) {
                setState(prev => {
                    const updated = new Map(prev)

                    newIds.forEach(id => {
                        const rawData = response.data?.[id]

                        if (rawData) {
                            const livePricingModel = getModel<LivePricingResponseTransport, LivePricingModel>(
                                rawData,
                                response.meta,
                            )

                            const formatted = livePricingFormatter.getAllFormattedData(livePricingModel)

                            updated.set(id, {
                                pricingDetails: formatted,
                                isLoading: false,
                                error: isNumber(formatted.errorMessage)
                                    ? Number(formatted.errorMessage)
                                    : LPS_ERROR_ENUM.GENERAL_ERROR,
                            })
                        } else {
                            updated.set(id, {
                                pricingDetails: null,
                                isLoading: false,
                                error: LPS_ERROR_ENUM.GENERAL_ERROR,
                            })
                        }
                    })

                    return updated
                })
            } else {
                throw new Error('No pricing data available.')
            }
        } catch (error: any) {
            setState(prev => {
                const updated = new Map(prev)
                newIds.forEach(id => {
                    updated.set(id, {
                        pricingDetails: null,
                        isLoading: false,
                        error: isNumber(error.message) ? error.message : LPS_ERROR_ENUM.GENERAL_ERROR,
                    })
                })
                return updated
            })
        }
    }, [loadedObjectIds, params, livePricingFormatter, isComboListing])

    useEffect(() => {
        if (isComboListing) {
            fetchComboPrices()
        }
    }, [fetchComboPrices, isComboListing])

    return {
        comboPrices: state,
    }
}
